import {z} from 'zod';

// Enums
export const CampaignStatusSchema = z.enum(['SCHEDULED', 'ACTIVE', 'ENDED']);
export type CampaignStatusValue = z.infer<typeof CampaignStatusSchema>;

// Shared by every endpoint whose only argument is the campaign id.
const CampaignIdArgSchema = z.object({
  id: z.string().uuid(),
});

// Schemas
export const ListCampaignResponseBodySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  ownerId: z.string().uuid(),
  validFrom: z.string(),
  validTo: z.string(),
  status: CampaignStatusSchema,
  position: z.number(),
});
export type ListCampaignResponseBody = z.infer<typeof ListCampaignResponseBodySchema>;

export const CampaignPositionRequestBodySchema = z.object({
  campaignId: z.string().uuid(),
  position: z.number().min(1),
});

export const ReorderCampaignsRequestBodySchema = z.object({
  positions: z.array(CampaignPositionRequestBodySchema),
  republishAds: z.boolean().optional(),
});

// listCampaigns
export const ListCampaignsApiResponseSchema = z.array(ListCampaignResponseBodySchema);
export type ListCampaignsApiResponse = z.infer<typeof ListCampaignsApiResponseSchema>;

// deleteCampaign
export const DeleteCampaignApiArgSchema = CampaignIdArgSchema.extend({
  republishAds: z.boolean().optional(),
});
export type DeleteCampaignApiArg = z.infer<typeof DeleteCampaignApiArgSchema>;

export const DeleteCampaignApiResponseSchema = z.null();
export type DeleteCampaignApiResponse = /** status 204 */ z.infer<
  typeof DeleteCampaignApiResponseSchema
>;

// reorderCampaigns
export const ReorderCampaignsApiArgSchema = z.object({
  reorderCampaignsRequestBody: ReorderCampaignsRequestBodySchema,
});
export type ReorderCampaignsApiArg = z.infer<typeof ReorderCampaignsApiArgSchema>;

export const ReorderCampaignsApiResponseSchema = z.null();
export type ReorderCampaignsApiResponse = /** status 204 */ z.infer<
  typeof ReorderCampaignsApiResponseSchema
>;

// Campaign detail / write contract
export const CampaignDiscountTypeSchema = z.enum(['PERCENT', 'ABSOLUTE']);

export const CampaignDiscountSchema = z.object({
  type: CampaignDiscountTypeSchema,
  amount: z.string(),
});

export const CampaignPromoPhotoSchema = z.object({
  fileId: z.string().uuid(),
  position: z.number().min(1),
});
export type CampaignPromoPhoto = z.infer<typeof CampaignPromoPhotoSchema>;

export const CampaignManuallySelectedVehicleSchema = z.object({
  vehicleId: z.string().uuid(),
  isIncluded: z.boolean(),
});
export type CampaignManuallySelectedVehicle = z.infer<typeof CampaignManuallySelectedVehicleSchema>;

export const CampaignFilterSchema = z.object({
  branchIds: z.array(z.string().uuid()).optional(),
  vehicleType: z.string().nullable().optional(),
  daysInStock: z.number().int().min(1).nullable().optional(),
  stockInDateFrom: z.string().nullable().optional(),
  mileageFrom: z.number().int().nullable().optional(),
  mileageTo: z.number().int().nullable().optional(),
  yearFrom: z.number().int().nullable().optional(),
  yearTo: z.number().int().nullable().optional(),
  priceFrom: z.number().nullable().optional(),
  priceTo: z.number().nullable().optional(),
  fuelTypes: z.array(z.string()).optional(),
  bodyStyles: z.array(z.string()).optional(),
  modelFamilies: z.array(z.string()).optional(),
  models: z.array(z.string()).optional(),
  makes: z.array(z.string()).optional(),
  vehicleConditions: z.array(z.string()).optional(),
  salePresetIds: z.array(z.string().uuid()).optional(),
  tagIds: z.array(z.string().uuid()).optional(),
});
export type CampaignFilter = z.infer<typeof CampaignFilterSchema>;

export const CampaignResponseBodySchema = ListCampaignResponseBodySchema.extend({
  watermarkId: z.string().uuid().nullable().optional(),
  promoPhotos: z.array(CampaignPromoPhotoSchema),
  keepExistingPromoPhotos: z.boolean(),
  discount: CampaignDiscountSchema.nullable(),
  warrantyProgramId: z.string().uuid().nullable(),
  filter: CampaignFilterSchema.nullish(),
  isAppliedToAllVehicles: z.boolean(),
  manuallySelectedVehicles: z.array(CampaignManuallySelectedVehicleSchema).nullish(),
});
export type CampaignResponseBody = z.infer<typeof CampaignResponseBodySchema>;

export const CampaignRequestBodySchema = z.object({
  name: z.string(),
  ownerId: z.string().uuid(),
  validFrom: z.string(),
  validTo: z.string(),
  watermarkId: z.string().uuid().nullable(),
  promoPhotos: z.array(CampaignPromoPhotoSchema),
  keepExistingPromoPhotos: z.boolean(),
  discount: CampaignDiscountSchema.nullable(),
  warrantyProgramId: z.string().uuid().nullable(),
  filter: CampaignFilterSchema,
  isAppliedToAllVehicles: z.boolean(),
  manuallySelectedVehicles: z.array(CampaignManuallySelectedVehicleSchema).nullish(),
  republishAds: z.boolean().optional(),
});
export type CampaignRequestBody = z.infer<typeof CampaignRequestBodySchema>;

// getCampaign
export const GetCampaignApiArgSchema = CampaignIdArgSchema;
export type GetCampaignApiArg = z.infer<typeof GetCampaignApiArgSchema>;

export type GetCampaignApiResponse = CampaignResponseBody;
export const GetCampaignApiResponseSchema = CampaignResponseBodySchema;

// createCampaign
export const UuidResponseBodySchema = z.object({
  id: z.string().uuid(),
});
export type UuidResponseBody = z.infer<typeof UuidResponseBodySchema>;

export const CreateCampaignApiArgSchema = z.object({
  createCampaignRequestBody: CampaignRequestBodySchema,
});
export type CreateCampaignApiArg = z.infer<typeof CreateCampaignApiArgSchema>;

export type CreateCampaignApiResponse = UuidResponseBody;
export const CreateCampaignApiResponseSchema = UuidResponseBodySchema;

// updateCampaign
export const UpdateCampaignApiArgSchema = z.object({
  id: z.string().uuid(),
  updateCampaignRequestBody: CampaignRequestBodySchema,
});
export type UpdateCampaignApiArg = z.infer<typeof UpdateCampaignApiArgSchema>;

export const UpdateCampaignApiResponseSchema = z.null();
export type UpdateCampaignApiResponse = /** status 204 */ z.infer<
  typeof UpdateCampaignApiResponseSchema
>;

// addVehicleToCampaign
export const AddVehicleToCampaignApiArgSchema = z.object({
  campaignId: z.string().uuid(),
  vehicleId: z.string().uuid(),
  republishAds: z.boolean().optional(),
});
export type AddVehicleToCampaignApiArg = z.infer<typeof AddVehicleToCampaignApiArgSchema>;

export const AddVehicleToCampaignApiResponseSchema = z.null();
export type AddVehicleToCampaignApiResponse = /** status 204 */ z.infer<
  typeof AddVehicleToCampaignApiResponseSchema
>;

// removeVehicleFromCampaign
export const RemoveVehicleFromCampaignApiArgSchema = AddVehicleToCampaignApiArgSchema;
export type RemoveVehicleFromCampaignApiArg = z.infer<typeof RemoveVehicleFromCampaignApiArgSchema>;

export const RemoveVehicleFromCampaignApiResponseSchema = z.null();
export type RemoveVehicleFromCampaignApiResponse = /** status 204 */ z.infer<
  typeof RemoveVehicleFromCampaignApiResponseSchema
>;

// listVehicleCampaigns
export const CampaignMembershipTypeSchema = z.enum(['MANUAL', 'FILTER', 'ALL_VEHICLES']);

export const VehicleCampaignResponseBodySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  validFrom: z.string(),
  validTo: z.string(),
  status: CampaignStatusSchema,
  membershipType: CampaignMembershipTypeSchema,
  isApplied: z.boolean(),
});
export type VehicleCampaignResponseBody = z.infer<typeof VehicleCampaignResponseBodySchema>;

export const ListVehicleCampaignsApiArgSchema = z.object({
  vehicleId: z.string().uuid(),
});
export type ListVehicleCampaignsApiArg = z.infer<typeof ListVehicleCampaignsApiArgSchema>;

export const ListVehicleCampaignsApiResponseSchema = z.array(VehicleCampaignResponseBodySchema);
export type ListVehicleCampaignsApiResponse = z.infer<typeof ListVehicleCampaignsApiResponseSchema>;
